<template>
    <div class="main-wrap courses-classes-page">
        <h1>{{ $t('general.courses_calendar') }}</h1>
        <Table
            class="class-table q-mt-lg"
            :columns="columns"
            :data="classes"
            :pagination="pagination"
            :filter="filter"
            :loading="isFetchingClasses"
            @request="onRequest"
        >
            <template v-slot:top>
                <div class="col-12">
                    <div class="row filter-wrap q-col-gutter-sm">
                        <div class="col-6 col-md-3">
                            <q-input
                                v-model="startDate"
                                :label="$tc('general.start_date')"
                                dense
                                outlined
                                clearable
                                @focus="$refs.startDate.show()"
                                @click="$refs.startDate.show()"
                            >
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy
                                            ref="startDate"
                                            transition-show="scale"
                                            transition-hide="scale"
                                        >
                                            <q-date
                                                v-model="filter.start_date"
                                                :locale="
                                                    locale.definition
                                                        .calendarPickerLocale
                                                        ? locale.definition
                                                              .calendarPickerLocale
                                                        : null
                                                "
                                                :mask="datePickerMaskFormat"
                                                @input="
                                                    () => $refs.startDate.hide()
                                                "
                                            />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>
                        <div class="col-6 col-md-3">
                            <q-input
                                v-model="endDate"
                                :label="$tc('general.end_date')"
                                dense
                                outlined
                                clearable
                                @focus="$refs.endDate.show()"
                                @click="$refs.endDate.show()"
                            >
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy
                                            ref="endDate"
                                            transition-show="scale"
                                            transition-hide="scale"
                                        >
                                            <q-date
                                                v-model="filter.end_date"
                                                :locale="
                                                    locale.definition
                                                        .calendarPickerLocale
                                                        ? locale.definition
                                                              .calendarPickerLocale
                                                        : null
                                                "
                                                :mask="datePickerMaskFormat"
                                                @input="
                                                    () => $refs.endDate.hide()
                                                "
                                            />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>
                        <div class="col-12 col-md-grow">
                            <q-input
                                v-model="filter.search"
                                debounce="300"
                                :label="$t('general.search')"
                                outlined
                                dense
                                clearable
                                :placeholder="$tc('general.course')"
                                @input="resetPagination"
                            >
                                <template v-slot:append>
                                    <q-icon name="search" />
                                </template>
                            </q-input>
                        </div>
                    </div>
                </div>
            </template>
        </Table>
    </div>
</template>

<script>
import Table from '@/components/ClassTable'
import classesService from '@/services/modules/classes'
//import coursesService from '@/services/modules/courses'
import store from '@/store'
import _ from 'lodash'
import moment from 'moment'

const loadingKeys = {
    fetchingClasses: 'fetching-classes',
}

export default {
    name: 'Classes',

    components: {
        Table,
    },

    data() {
        return {
            classes: [],

            pagination: {
                sortBy: 'created_at',
                descending: true,
                page: 1,
                rowsPerPage: 20,
                rowsNumber: 0,
            },
            filter: {
                search: '',
                start_date: null,
                end_date: null,
            },
        }
    },

    computed: {
        datePickerMaskFormat() {
            return this.locale &&
                this.locale.definition &&
                this.locale.definition.dateFormat
                ? this.locale.definition.dateFormat
                : 'DD/MM/YYYY'
        },

        columns() {
            return [
                {
                    name: 'code',
                    label: this.$t('general.class_code'),
                    field: 'code',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'title',
                    label: this.$t('general.course'),
                    field: (row) => row.courses.title,
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'start_date',
                    label: this.$tc('general.start_date'),
                    field: 'start_date',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'end_date',
                    label: this.$t('general.end_date'),
                    field: 'end_date',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'trainers',
                    label: this.$t('general.trainers'),
                    field: 'trainers',
                    align: 'left',
                    sortable: false,
                },
                {
                    name: 'vacancies',
                    label:
                        this.$t('general.enrolls_available') +
                        '/' +
                        this.$t('general.vacancies'),
                    field: 'vacancies',
                    align: 'left',
                    sortable: false,
                },
            ]
        },

        isFetchingClasses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingClasses
            ).length
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        startDate: {
            get: function () {
                return this.filter.start_date || null
            },
            set: function (value) {
                if (value === null) {
                    return (this.filter.start_date = null)
                }
                return this.filter.start_date || null
            },
        },

        endDate: {
            get: function () {
                return this.filter.end_date || null
            },
            set: function (value) {
                if (value === null) {
                    return (this.filter.end_date = null)
                }
                return this.filter.end_date || null
            },
        },
    },

    watch: {
        locale(newValue, oldValue) {
            this.convertDates(newValue, oldValue)
        },
    },

    mounted() {
        this.filter.start_date = moment().format(this.datePickerMaskFormat)
    },

    methods: {
        convertDates(newLocale, oldLocale) {
            this.filter.start_date = this.filter.start_date
                ? moment(
                      this.filter.start_date,
                      oldLocale.definition.dateFormat
                  ).format(newLocale.definition.dateFormat)
                : this.filter.start_date
            this.filter.end_date = this.filter.end_date
                ? moment(
                      this.filter.end_date,
                      oldLocale.definition.dateFormat
                  ).format(newLocale.definition.dateFormat)
                : this.filter.end_date
        },

        resetPagination() {
            this.pagination.page = 1
            this.fetchClasses()
        },

        onRequest(paginationAux, filterAux) {
            this.filter = filterAux
            this.pagination = paginationAux
            this.fetchClasses()
        },

        mapColumn(value) {
            return value == null ? this.pagination.sortBy : value
        },

        mapOrder(value) {
            return value == true ? 'desc' : 'asc'
        },

        async fetchClasses() {
            this.loadingAdd(loadingKeys.fetchingClasses)

            let parameters = {
                filter: {
                    date: {
                        begin: this.filter.start_date
                            ? moment(
                                  this.filter.start_date,
                                  this.datePickerMaskFormat
                              ).format('YYYY-MM-DD')
                            : null,
                        end: this.filter.end_date
                            ? moment(
                                  this.filter.end_date,
                                  this.datePickerMaskFormat
                              ).format('YYYY-MM-DD')
                            : null,
                    },
                    search: this.filter.search,
                    active: true,
                },
                pagination: {
                    perPage: this.pagination.rowsPerPage,
                    currentPage: this.pagination.page,
                },
                orderBy: [
                    {
                        column: this.mapColumn(this.pagination.sortBy),
                        direction: this.mapOrder(this.pagination.descending),
                    },
                ],
                relations: {
                    status: true,
                    businessunits: true,
                    lessons: true,
                    courses: true,
                    countActiveEnrollments: true,
                },
            }

            let response = await classesService.retrieve(parameters)

            if (response.status === 200) {
                let classes = response.data.results

                for (let index = 0; index < classes.length; index++) {
                    let trainers = classes[index].lessons.map((lesson) =>
                        lesson.lessonTrainers.map((trainer) => trainer.trainers)
                    )
                    classes[index].trainers = _.uniqBy(
                        _.flattenDeep(trainers),
                        'id'
                    )
                }
                //let courses = []

                // for (let index = 0; index < classes.length; index++) {
                //     let classInfo = {
                //         class_id: classes[index].class_id,
                //         code: classes[index].code,
                //         start_date: classes[index].start_date,
                //         end_date: classes[index].end_date,
                //         vacancies: classes[index].vacancies,
                //     }

                //     let courseParameters = {
                //         filter: {
                //             id: classes[index].course_id,
                //             search: this.filter.search,
                //         },
                //         relations: {
                //             courses_coursetype: true,
                //             courses_packagetypes: true,
                //         },
                //     }
                //     let courseResponse = await coursesService.retrieve(
                //         courseParameters
                //     )

                //     if (
                //         courseResponse.status === 200 &&
                //         courseResponse.data.results.length >= 1
                //     ) {
                //         let classThumb = {
                //             ...classInfo,
                //             ...courseResponse.data.results[0],
                //         }
                //         courses.push(classThumb)
                //     }
                // }

                this.classes = _.cloneDeep(classes)
                this.pagination.rowsNumber = response.data.pagination.total
            }

            this.loadingRemove(loadingKeys.fetchingClasses)
        },
    },
}
</script>
